import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { APP_SETTING } from '@shared/types';
import { createFileRoute } from '@tanstack/react-router';
import { Card, Form, InputNumber, Select } from 'antd';
import { useEffect, useState } from 'react';

type State = {
    notificationDisabled: string;
    indicatorProcessingBatchSize: string;
    upgradeInProgress: string;
    disablePublicFormCaptcha: string;
    autoDetectSimilaritySearchLimit: string;
    autoDetectSimilarityThreshold: string;
};

/** Parse the JSON-encoded email list app setting into a string array (tolerant of empty/malformed). */
function parseEmailList(value: string | undefined | null): string[] {
    if (!value) return [];
    try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed.filter((e): e is string => typeof e === 'string') : [];
    } catch {
        return [];
    }
}

export const Route = createFileRoute('/admin-console/advanced/app-settings/well-known')({
    staticData: {
        breadcrumb: {
            title: 'App settings',
        },
        ui: {
            title: 'App settings',
        },
    },
    component: AppSettings,
});

function AppSettings() {
    const [state, setState] = useState<State>({
        notificationDisabled: 'false',
        indicatorProcessingBatchSize: '5',
        upgradeInProgress: 'false',
        disablePublicFormCaptcha: 'false',
        autoDetectSimilaritySearchLimit: '5',
        autoDetectSimilarityThreshold: '0.5',
    });
    const [systemAlertEmails, setSystemAlertEmails] = useState<string[]>([]);
    const { trpcClient } = useTrpc();
    const [processing, setProcessing] = useState(false);
    const [loaded, setLoaded] = useState(false);

    const [form] = Form.useForm();

    const load = async () => {
        try {
            // Load all settings in parallel for better performance
            const [
                notificationDisabledRes,
                indicatorProcessingBatchSizeRes,
                upgradeInProgressRes,
                disablePublicFormCaptchaRes,
                autoDetectSimilaritySearchLimitRes,
                autoDetectSimilarityThresholdRes,
                systemAlertEmailsRes,
            ] = await Promise.all([
                trpcClient.admin.appSetting.getAppSettingByName.query({ name: APP_SETTING.NOTIFICATION_DISABLED }),
                trpcClient.admin.appSetting.getAppSettingByName.query({
                    name: APP_SETTING.INDICATOR_PROCESSING_BATCH_SIZE,
                }),
                trpcClient.admin.appSetting.getAppSettingByName.query({ name: APP_SETTING.UPGRADE_IN_PROGRESS }),
                trpcClient.admin.appSetting.getAppSettingByName.query({
                    name: APP_SETTING.DISABLE_PUBLIC_FORM_CAPTCHA,
                }),
                trpcClient.admin.appSetting.getAppSettingByName.query({
                    name: APP_SETTING.AUTO_DETECT_SIMILARITY_SEARCH_LIMIT,
                }),
                trpcClient.admin.appSetting.getAppSettingByName.query({
                    name: APP_SETTING.AUTO_DETECT_SIMILARITY_THRESHOLD,
                }),
                trpcClient.admin.appSetting.getAppSettingByName.query({
                    name: APP_SETTING.SYSTEM_ALERT_NOTIFICATION_EMAILS,
                }),
            ]);

            const newState = {
                indicatorProcessingBatchSize: indicatorProcessingBatchSizeRes?.value ?? '',
                notificationDisabled: notificationDisabledRes?.value ?? '',
                upgradeInProgress: upgradeInProgressRes?.value ?? '',
                // Absent means captcha is enforced — mirror the server's fail-closed default.
                disablePublicFormCaptcha: disablePublicFormCaptchaRes?.value || 'false',
                autoDetectSimilaritySearchLimit: autoDetectSimilaritySearchLimitRes?.value ?? '',
                autoDetectSimilarityThreshold: autoDetectSimilarityThresholdRes?.value ?? '',
            };
            setState(newState);
            setSystemAlertEmails(parseEmailList(systemAlertEmailsRes?.value));
            form.setFieldsValue(newState);
            setLoaded(true);
        } catch (e) {
            errorMessage.show(e);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const save = async () => {
        try {
            setProcessing(true);
            message.loading('Saving...');

            await Promise.all([
                trpcClient.admin.appSetting.updateAppSetting.mutate({
                    name: APP_SETTING.NOTIFICATION_DISABLED,
                    value: state.notificationDisabled,
                    format: 'PLAIN_TEXT',
                }),
                trpcClient.admin.appSetting.updateAppSetting.mutate({
                    name: APP_SETTING.INDICATOR_PROCESSING_BATCH_SIZE,
                    value: state.indicatorProcessingBatchSize,
                    format: 'PLAIN_TEXT',
                }),
                trpcClient.admin.appSetting.updateAppSetting.mutate({
                    name: APP_SETTING.UPGRADE_IN_PROGRESS,
                    value: state.upgradeInProgress,
                    format: 'PLAIN_TEXT',
                }),
                trpcClient.admin.appSetting.updateAppSetting.mutate({
                    name: APP_SETTING.DISABLE_PUBLIC_FORM_CAPTCHA,
                    value: state.disablePublicFormCaptcha,
                    format: 'PLAIN_TEXT',
                }),
                trpcClient.admin.appSetting.updateAppSetting.mutate({
                    name: APP_SETTING.AUTO_DETECT_SIMILARITY_SEARCH_LIMIT,
                    value: state.autoDetectSimilaritySearchLimit,
                    format: 'PLAIN_TEXT',
                }),
                trpcClient.admin.appSetting.updateAppSetting.mutate({
                    name: APP_SETTING.AUTO_DETECT_SIMILARITY_THRESHOLD,
                    value: state.autoDetectSimilarityThreshold,
                    format: 'PLAIN_TEXT',
                }),
                trpcClient.admin.appSetting.updateAppSetting.mutate({
                    name: APP_SETTING.SYSTEM_ALERT_NOTIFICATION_EMAILS,
                    value: JSON.stringify(systemAlertEmails),
                    format: 'JSON',
                }),
            ]);

            message.success('Saved.');
        } catch (e) {
            errorMessage.show(e);
        } finally {
            setProcessing(false);
        }
    };

    return (
        <PageController route={Route}>
            <div className="mx-auto max-w-5xl">
                <Form
                    form={form}
                    disabled={!loaded || processing}
                    layout="horizontal"
                    labelCol={{ span: 8 }}
                    wrapperCol={{ span: 16 }}
                    onFinish={save}
                    onFinishFailed={(errorInfo) => {
                        form.scrollToField(errorInfo.errorFields[0].name);
                    }}
                >
                    <Card>
                        <div>
                            <Form.Item
                                label={<span className="font-medium">Notification Disabled</span>}
                                required
                                rules={[
                                    {
                                        validator: async () => {
                                            if (!state.notificationDisabled) throw new Error('Field is required');
                                        },
                                    },
                                ]}
                            >
                                <Select
                                    value={state.notificationDisabled}
                                    onChange={(value) => {
                                        setState((prev) => ({ ...prev, notificationDisabled: value }));
                                    }}
                                    className="w-48"
                                >
                                    <Select.Option value="true">Enabled</Select.Option>
                                    <Select.Option value="false">Disabled</Select.Option>
                                </Select>
                            </Form.Item>

                            <Form.Item
                                label={<span className="font-medium">Indicator Processing Batch Size</span>}
                                required
                                rules={[
                                    {
                                        validator: async () => {
                                            if (!state.indicatorProcessingBatchSize)
                                                throw new Error('Field is required');
                                        },
                                    },
                                ]}
                            >
                                <InputNumber
                                    value={Number(state.indicatorProcessingBatchSize)}
                                    min={1}
                                    onChange={(value) => {
                                        setState((prev) => ({
                                            ...prev,
                                            indicatorProcessingBatchSize: (value || 5).toString(),
                                        }));
                                    }}
                                    className="w-48"
                                />
                            </Form.Item>

                            <Form.Item
                                label={<span className="font-medium">Upgrade In Progress</span>}
                                required
                                rules={[
                                    {
                                        validator: async () => {
                                            if (!state.upgradeInProgress) throw new Error('Field is required');
                                        },
                                    },
                                ]}
                            >
                                <Select
                                    value={state.upgradeInProgress}
                                    onChange={(value) => {
                                        setState((prev) => ({ ...prev, upgradeInProgress: value }));
                                    }}
                                    className="w-48"
                                >
                                    <Select.Option value="true">Yes</Select.Option>
                                    <Select.Option value="false">No</Select.Option>
                                </Select>
                            </Form.Item>

                            <Form.Item
                                label={<span className="font-medium">Captcha verification</span>}
                                required
                                help="Turn off only to ride out a captcha-vendor outage — it disables verification on every public form and on password reset."
                            >
                                <Select
                                    value={state.disablePublicFormCaptcha}
                                    onChange={(value) => {
                                        setState((prev) => ({ ...prev, disablePublicFormCaptcha: value }));
                                    }}
                                    className="w-48"
                                >
                                    <Select.Option value="false">Enforced</Select.Option>
                                    <Select.Option value="true">Disabled</Select.Option>
                                </Select>
                            </Form.Item>

                            <Form.Item
                                label={<span className="font-medium">Auto Detect Similarity Threshold</span>}
                                required
                                help="How similar the email must be to the regulation to be included in the auto-detect context"
                            >
                                <InputNumber
                                    value={Number(state.autoDetectSimilarityThreshold)}
                                    min={0}
                                    max={1}
                                    step={0.1}
                                    onChange={(value) => {
                                        setState((prev) => ({
                                            ...prev,
                                            autoDetectSimilarityThreshold: (value || 0.5).toString(),
                                        }));
                                    }}
                                    className="w-48"
                                />
                            </Form.Item>
                            <Form.Item
                                label={<span className="font-medium">Auto Detect Similarity Search Limit</span>}
                                required
                                help="How many documents to include in the auto-detect context"
                            >
                                <InputNumber
                                    value={Number(state.autoDetectSimilaritySearchLimit)}
                                    min={1}
                                    onChange={(value) => {
                                        setState((prev) => ({
                                            ...prev,
                                            autoDetectSimilaritySearchLimit: (value || 5).toString(),
                                        }));
                                    }}
                                    className="w-48"
                                />
                            </Form.Item>
                            <Form.Item
                                label={<span className="font-medium">System alert notification emails</span>}
                                help="Recipients for system alert emails, such as upcoming AI model expiry. Press enter to add each address."
                            >
                                <Select
                                    mode="tags"
                                    value={systemAlertEmails}
                                    onChange={setSystemAlertEmails}
                                    placeholder="Add email addresses"
                                    tokenSeparators={[',', ' ']}
                                    open={false}
                                    className="w-full max-w-lg"
                                />
                            </Form.Item>
                        </div>
                    </Card>

                    <div className="mt-4 flex justify-end">
                        <Button type="primary" htmlType="submit" loading={processing} disabled={processing || !loaded}>
                            Save Changes
                        </Button>
                    </div>
                </Form>
            </div>
        </PageController>
    );
}
